import React, { PropsWithChildren, useCallback, useMemo } from 'react';
import { useOffresCtx, useOriginesOffresCtx } from '../../../providers';
import { TiersOfferOrigins, useOffer } from '../hooks';
import { Offre } from '../../../providers/OffresProvider';
import TiersOfferContext from './OfferContext';

const TiersOfferProvider: React.FunctionComponent<PropsWithChildren> = function ({ children }) {
    const { isDataLoaded: isOffresLoaded, getOffres } = useOffresCtx();
    const { isDataLoaded: isOriginesOffresLoaded, getOriginesOffres } = useOriginesOffresCtx();
    const offerDataLoaded = isOffresLoaded() && isOriginesOffresLoaded();

    const {
        getTiersOfferOrigins,
        createTiersOfferOrigin,
        updateTiersOfferOrigin,
        deleteTiersOfferOrigin,
    } = useOffer();

    const getTiersCurrentOffer = useCallback(
        (tiersId: number): Promise<Offre | undefined> =>
            new Promise((resolve, reject) => {
                getTiersOfferOrigins(tiersId)
                    .then((tiersOffresOrigines) => {
                        if (!tiersOffresOrigines[0]) {
                            resolve(undefined);

                            return;
                        }

                        const originOfferId = tiersOffresOrigines[0].origineOffreId;
                        const originesOffres = getOriginesOffres();
                        const offerOrigin = originesOffres.find(
                            (item) => item.id === originOfferId,
                        );
                        const offer = getOffres().find(
                            (item) => offerOrigin && item.id === offerOrigin.offreId,
                        );

                        resolve(offer);
                    })
                    .catch(reject);
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [offerDataLoaded],
    );

    const getTiersCurrentOfferWithDefault = useCallback(
        (tiersId: number): Promise<Offre | undefined> =>
            new Promise((resolve, reject) => {
                getTiersCurrentOffer(tiersId)
                    .then((offerOrigineTiers: Offre | undefined) => {
                        if (!offerOrigineTiers) {
                            const originesOffres = getOriginesOffres();
                            const offerOrigin = originesOffres.find(
                                (item) => item.default === true,
                            );
                            const offer = getOffres().find(
                                (item) => offerOrigin && item.id === offerOrigin.offreId,
                            );
                            resolve(offer);

                            return;
                        }
                        resolve(offerOrigineTiers);
                    })
                    .catch(reject);
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [offerDataLoaded],
    );

    const setTiersCurrentOffer = useCallback(
        (tiersId: number, newOfferId?: number): Promise<TiersOfferOrigins | undefined> =>
            new Promise((resolve, reject) => {
                getTiersOfferOrigins(tiersId)
                    .then((tiersOffresOrigines) => {
                        const originesOffres = getOriginesOffres();

                        if (!tiersOffresOrigines.length && newOfferId) {
                            const offerOrigin = originesOffres.find(
                                (item) => item.offreId === newOfferId,
                            );

                            if (offerOrigin) {
                                createTiersOfferOrigin(tiersId, offerOrigin.id)
                                    .then(resolve)
                                    .catch(reject);
                            }

                            return;
                        }

                        if (tiersOffresOrigines.length && newOfferId) {
                            const offerOrigin = originesOffres.find(
                                (item) => item.offreId === newOfferId,
                            );

                            if (offerOrigin) {
                                updateTiersOfferOrigin(
                                    tiersOffresOrigines[0].id,
                                    tiersId,
                                    offerOrigin.id,
                                )
                                    .then(resolve)
                                    .catch(reject);
                            }

                            return;
                        }

                        if (tiersOffresOrigines.length && !newOfferId) {
                            deleteTiersOfferOrigin(tiersOffresOrigines[0].id)
                                .then(() => resolve(undefined))
                                .catch(reject);
                        }
                    })
                    .catch(reject);
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [offerDataLoaded],
    );

    const contextValue = useMemo(
        () => ({
            offerDataLoaded,
            getTiersCurrentOffer,
            getTiersCurrentOfferWithDefault,
            setTiersCurrentOffer,
        }),
        [
            offerDataLoaded,
            getTiersCurrentOffer,
            getTiersCurrentOfferWithDefault,
            setTiersCurrentOffer,
        ],
    );

    return <TiersOfferContext.Provider value={contextValue}>{children}</TiersOfferContext.Provider>;
};

export { TiersOfferProvider };
export default TiersOfferProvider;
